import { useTranslation } from 'react-i18next'

import { NOTIFICATION_TYPE } from '../../../constants/common'
import { REQUEST_PROFILE_CHANGE_TYPE } from '../../../constants/request'
import { ROUTE_PATH_LIST } from '../../../constants/route'
import { useInitData, useNavigateBack, useScreen } from '../../../hooks'
import { BUTTON_HEIGHT_40 } from '../../Button'
import {
  EntityProfileHeader,
  EntityProfileHeaderEditTitleModal,
  EntityProfileHeaderTitleWithStatus,
} from '../../Entity'
import { ICON_HEIGHT_24 } from '../../Icon'
import { NotificationInline } from '../../Notification'
import {
  useConnectedRequestsPreview,
  useRequestProfileContext,
} from '../RequestProfile.hooks'
import { RequestProfileActionsWidget } from '../RequestProfileActionsWidget'
import { RequestProfileHeaderContent } from '../RequestProfileHeaderContent'
import { RequestProfileHeaderEditTitleField } from '../RequestProfileHeaderEditTitleField'
import { RequestProfileHeaderFooter } from '../RequestProfileHeaderFooter'
import { RequestProfileHeaderInfoItems } from '../RequestProfileHeaderInfoItems'
import { RequestProfileHeaderStatusSelect } from '../RequestProfileHeaderStatusSelect'
import { RequestProfileWidgetAISummary } from '../RequestProfileWidget'

import { REQUEST_PROFILE_HEADER_EDIT_TITLE_BTN_QA_NAME } from './RequestProfileHeader.constants'
import { useRequestProfileHeaderInfo } from './RequestProfileHeader.hooks'
import {
  getRequestProfileHeaderEditTitleInitialValues,
  getRequestProfileHeaderEditTitleValidationSchema,
} from './RequestProfileHeader.utils'

import * as styles from './RequestProfileHeader.module.css'

import ServiceRequestIcon from '../../../icons/service-request.svg'

function RequestProfileHeader({
  Actions,
  BackButton,
  ToggleButton,
  actionsProps,
  requestTitle,
  stickyTitleClassName,
  toggleButtonProps,
}) {
  const {
    handleCloseModal,
    handleReloadAfterChange,
    handleUpdateEntity,
    isInitiated,
    isPreview,
    outboundUuid,
    relatedRequestsCount,
    requestData,
    setModal,
  } = useRequestProfileContext()

  const { t } = useTranslation()

  const { ai_features: aiFeatures } = useInitData()

  const { isMobileScreenSize } = useScreen()

  const isHeaderGroupLayout = isPreview || isMobileScreenSize

  const hasAiSummary = !outboundUuid && aiFeatures?.request_summary_generation

  const { infoItemsProps } = useRequestProfileHeaderInfo()

  const sectionBackUrl = `/${ROUTE_PATH_LIST.serviceRequests}`

  const handleClickBackButton = useNavigateBack(sectionBackUrl)

  async function handleReloadAfterSuccess() {
    return handleReloadAfterChange(
      REQUEST_PROFILE_CHANGE_TYPE.providers,
      REQUEST_PROFILE_CHANGE_TYPE.relatedRequests
    )
  }

  const { handleClickOpenPreview } = useConnectedRequestsPreview({
    requestData,
    onCloseModal: handleCloseModal,
    onMassEditSuccess: handleReloadAfterSuccess,
    onSetModal: setModal,
  })

  function handleEditTitleSubmit({ value }) {
    return handleUpdateEntity(value).then(handleCloseModal)
  }

  function handleOpenEditTitleModal() {
    setModal(
      <EntityProfileHeaderEditTitleModal
        Fields={RequestProfileHeaderEditTitleField}
        initialValues={getRequestProfileHeaderEditTitleInitialValues(
          requestData?.title
        )}
        validationSchema={getRequestProfileHeaderEditTitleValidationSchema(t)}
        onClose={handleCloseModal}
        onSubmit={handleEditTitleSubmit}
      />
    )
  }

  const headerProps = {
    Actions,
    BackButton: isHeaderGroupLayout ? null : BackButton,
    Status: RequestProfileHeaderStatusSelect,
    ToggleButton,
    actionsProps,
    backButtonProps: {
      height: BUTTON_HEIGHT_40,
    },
    className: styles.title,
    entityAvatarProps: {
      Icon: ServiceRequestIcon,
      iconProps: {
        svgSize: ICON_HEIGHT_24,
      },
      tooltip: t('Request.ProfileHeaderServiceRequestIconTooltipKey'),
    },
    isArchived: requestData?.isArchived,
    openClassName: styles.titleOpen,
    sectionBackUrl,
    statusProps: {
      className: styles.statusSelect,
      disabled: !requestData?.permissions?.can_edit_status,
    },
    text: requestTitle,
    textPlaceholder: t('Request.ProfileHeaderNoTitleKey'),
    textProps: {
      editable:
        !requestData?.isArchived && requestData?.permissions?.can_edit_content,
      editButtonQaName: REQUEST_PROFILE_HEADER_EDIT_TITLE_BTN_QA_NAME,
    },
    toggleButtonProps: {
      ...toggleButtonProps,
      height: BUTTON_HEIGHT_40,
    },
  }

  const profileHeader = (
    <EntityProfileHeader
      withPageStickyTitle
      Content={RequestProfileHeaderContent}
      Footer={RequestProfileHeaderFooter}
      Header={EntityProfileHeaderTitleWithStatus}
      InfoItems={RequestProfileHeaderInfoItems}
      bodyContentClassName={styles.bodyContent}
      headerProps={headerProps}
      infoItemsProps={infoItemsProps}
      isLoading={!isInitiated}
      stickyTitleClassName={stickyTitleClassName}
      onClickEditTitleButton={handleOpenEditTitleModal}
      onCloseModal={handleCloseModal}
      onSetModal={setModal}
    />
  )

  return (
    <>
      {relatedRequestsCount > 0 ? (
        <NotificationInline
          actions={[
            {
              callback: handleClickOpenPreview,
              text: t('Request.OpenPreviewTextBtnKey'),
            },
          ]}
          text={t('Request.HasRelatedRequestsTitleKey')}
          type={NOTIFICATION_TYPE.warning}
        />
      ) : null}
      {isHeaderGroupLayout ? (
        <div className={styles.mobileHeaderGroup}>
          <RequestProfileActionsWidget
            className={styles.mobileActionsBar}
            isSkeleton={!isInitiated}
            onClickBack={isPreview ? null : handleClickBackButton}
          />
          {hasAiSummary ? (
            <RequestProfileWidgetAISummary
              className={styles.mobileAiSummaryWidget}
              isSkeleton={!isInitiated}
              requestData={requestData}
            />
          ) : null}
          {profileHeader}
        </div>
      ) : (
        profileHeader
      )}
    </>
  )
}

export default RequestProfileHeader
